import React from 'react';
import CallRecordTypeIcon from 'flow/components/custom-components/CallRecordTypeIcon';
import {
    callRecordIsIncoming,
    getCallRecordInCallDuration,
    getPersonalCallRecordTimestampFormatted,
    callRecordVoiceMessageIsUnread,
} from 'flow/utils/call-records-utils';
import { getHoursMinutesSecondsUiFriendlyFromMilliseconds } from 'flow/utils/date-time-utils';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { CallRecordKey } from 'flow/types/EntityKeys';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import { twMerge } from 'tailwind-merge';

interface Props {
    callRecordKey: CallRecordKey;
    callRecordNumberPrettyFormatted: string;
}

const CallRecordingPageItem = ({
    callRecordKey,
    callRecordNumberPrettyFormatted,
}: Props) => {
    const { i18next } = useAppContext();
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );

    if (!callRecord) return null;

    const callDurationMilliseconds = getCallRecordInCallDuration(callRecord);
    const durationString = getHoursMinutesSecondsUiFriendlyFromMilliseconds(
        callDurationMilliseconds,
        i18next,
    );

    const callType = callRecordIsIncoming(callRecord)
        ? i18next.t('call.incoming')
        : i18next.t('call.outgoing');

    const directionText = callRecordIsIncoming(callRecord)
        ? i18next.t('call.from')
        : i18next.t('call.to');

    return (
        <div className="flex flex-col items-center py-2">
            <div className="flex w-full flex-col gap-y-2 truncate">
                <div className="flex flex-row items-end">
                    <CallRecordTypeIcon callRecordKey={callRecordKey} />
                    <div className="flex w-full justify-between truncate text-gray-800">
                        <div
                            className={twMerge(
                                'text-sm',
                                callRecordVoiceMessageIsUnread(callRecord)
                                    ? 'font-bold'
                                    : '',
                            )}
                        >
                            {callType}
                        </div>
                        <div className="text-xs">
                            {getPersonalCallRecordTimestampFormatted(
                                callRecord,
                            )}
                        </div>
                    </div>
                </div>

                <div className="ms-9 flex flex-row text-sm text-gray-500">
                    {durationString}
                </div>

                <div className="ms-9 flex flex-row gap-x-2 text-xs">
                    <div>{directionText}</div>
                    <div className="flex truncate text-gray-500">
                        {callRecordNumberPrettyFormatted}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default React.memo(CallRecordingPageItem);
